import type {
  ExistingTreeRepairPlanGrouping,
} from '@/governor/tree-system/ExistingTreeRepairPlanGrouping';
import type {
  ExistingTreeRepairPlan,
  ExistingTreeRepairPlanItem,
} from '@/governor/tree-system/ExistingTreeRepairPlan';
import type {
  ExistingTreeRepairPlanSummary,
} from '@/governor/tree-system/ExistingTreeRepairPlanSummary';
import type {
  ExistingTreeRepairRendererCertification,
} from '@/governor/tree-system/ExistingTreeRepairRendererCertification';
import {
  buildExistingTreeRepairBrowserEvidence,
} from '@/governor/tree-system/ExistingTreeRepairBrowserEvidence';

interface TreeStructuralRepairReviewProps {
  summary?: ExistingTreeRepairPlanSummary | null;
  grouping?: ExistingTreeRepairPlanGrouping | null;
  plan?: ExistingTreeRepairPlan | null;
  certification?: ExistingTreeRepairRendererCertification | null;
  generatedAt?: string | null;
  effectiveOwnerUserId?: string | null;
  source?: 'FamilyGraphContext' | null;
  mode?: 'dry-run' | null;
}

function formatLabel(value: string): string {
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase(),
    );
}

function RepairItem({
  item,
}: {
  item: ExistingTreeRepairPlanItem;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-semibold text-slate-900">
          {item.memberName}
        </p>
        <span className="rounded-full border border-slate-300 bg-slate-50 px-2 py-1 text-xs text-slate-700">
          {formatLabel(item.severity)}
        </span>
        <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-1 text-xs text-violet-800">
          {formatLabel(item.category)}
        </span>
      </div>

      <p className="mt-3 text-sm text-slate-700">
        {item.summary}
      </p>

      <div className="mt-3 text-sm text-slate-700">
        <p>
          <b>Finding:</b>{' '}
          {formatLabel(item.findingKind)}
        </p>

        <p>
          <b>Required approvals:</b>{' '}
          {item.requiredApprovals
            .map(formatLabel)
            .join(', ')}
        </p>

        <p>
          <b>Related IDs:</b>{' '}
          {item.relatedIds.length > 0
            ? item.relatedIds.join(', ')
            : 'None'}
        </p>

        <p>
          <b>Execution allowed:</b> No
        </p>
      </div>
    </div>
  );
}

export default function TreeStructuralRepairReview({
  summary,
  grouping,
  plan,
  certification,
  generatedAt,
  effectiveOwnerUserId,
  source: projectionSource,
  mode,
}: TreeStructuralRepairReviewProps) {
  if (!summary && !grouping && !plan) {
    return (
      <section className="rounded-[2rem] border border-slate-200 bg-white p-6">
        <h2 className="text-3xl">
          Tree Structural Repair Review
        </h2>
        <p className="mt-4 text-slate-600">
          Waiting for the governed Tree repair-plan
          projection.
        </p>
      </section>
    );
  }

  const canExportEvidence = Boolean(
    summary &&
    grouping &&
    plan &&
    certification &&
    generatedAt &&
    projectionSource === 'FamilyGraphContext' &&
    mode === 'dry-run',
  );

  const downloadEvidence = () => {
    if (
      !canExportEvidence ||
      !summary ||
      !grouping ||
      !plan ||
      !certification ||
      !generatedAt
    ) {
      return;
    }

    const evidence =
      buildExistingTreeRepairBrowserEvidence({
        generatedAt,
        effectiveOwnerUserId:
          effectiveOwnerUserId ?? null,
        source: 'FamilyGraphContext',
        mode: 'dry-run',
        report: plan,
        summary,
        grouping,
        rendererCertification:
          certification,
        capturedFromUrl:
          window.location.href,
        capturedAt:
          new Date().toISOString(),
      });

    const blob = new Blob(
      [
        JSON.stringify(
          evidence,
          null,
          2,
        ),
      ],
      {
        type: 'application/json',
      },
    );

    const url =
      URL.createObjectURL(blob);

    const anchor =
      document.createElement('a');

    anchor.href = url;
    anchor.download =
      `kinscircle-tree-repair-evidence-${
        evidence.capturedAt
          .replace(/[:.]/g, '-')
      }.json`;

    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    URL.revokeObjectURL(url);
  };

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-6">
      <h2 className="text-3xl">
        Tree Structural Repair Review
      </h2>

      {certification && (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-800">
          <p>
            <b>Renderer status:</b>{' '}
            {formatLabel(certification.status)}
          </p>
          <p>
            <b>Live projection received:</b>{' '}
            {certification.liveProjectionReceived
              ? 'Yes'
              : 'No'}
          </p>
          <p>
            <b>Member names visible:</b>{' '}
            {certification.memberNamesVisible
              ? 'Yes'
              : 'No'}
          </p>
          <p>
            <b>Approvals visible:</b>{' '}
            {certification.approvalsVisible
              ? 'Yes'
              : 'No'}
          </p>
          <p>
            <b>Certification allowed:</b> No
          </p>
        </div>
      )}

      {summary && (
        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm text-slate-600">
              Total items
            </p>
            <p className="text-3xl font-semibold">
              {summary.totalItems}
            </p>
          </div>

          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
            <p className="text-sm text-rose-800">
              Blocking
            </p>
            <p className="text-3xl font-semibold text-rose-900">
              {summary.blockingItems}
            </p>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm text-amber-800">
              Warnings
            </p>
            <p className="text-3xl font-semibold text-amber-900">
              {summary.warningItems}
            </p>
          </div>

          <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4">
            <p className="text-sm text-sky-800">
              Informational
            </p>
            <p className="text-3xl font-semibold text-sky-900">
              {summary.informationalItems}
            </p>
          </div>
        </div>
      )}

      {grouping?.categoryGroups?.length ? (
        <div className="mt-6 space-y-5">
          {grouping.categoryGroups.map((group) => (
            <section
              key={group.category}
              className="rounded-2xl border border-violet-200 bg-violet-50 p-5"
            >
              <h3 className="text-xl font-semibold text-violet-950">
                {formatLabel(group.category)}
              </h3>

              <p className="mt-1 text-sm text-violet-800">
                {group.itemCount} governed review item
                {group.itemCount === 1 ? '' : 's'}
              </p>

              <div className="mt-4 grid gap-3">
                {group.items.map((item) => (
                  <RepairItem
                    key={`${group.category}-${item.memberId}-${item.findingKind}`}
                    item={item}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : plan?.items?.length ? (
        <div className="mt-6 grid gap-3">
          {plan.items.map((item) => (
            <RepairItem
              key={`${item.memberId}-${item.findingKind}`}
              item={item}
            />
          ))}
        </div>
      ) : (
        <p className="mt-5 text-slate-600">
          No structural repair items are currently
          projected.
        </p>
      )}

      <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        This review is read-only. Automatic repair,
        execution, and production writes remain disabled.
      </div>

      <button
        type="button"
        onClick={downloadEvidence}
        disabled={!canExportEvidence}
        className="mt-4 rounded-2xl border border-violet-300 bg-violet-50 px-4 py-3 font-semibold text-violet-900 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Download Tree browser evidence
      </button>

      <p className="mt-2 text-sm text-slate-600">
        This download captures the current projected review only.
        It does not certify repairs or enable production writes.
      </p>
    </section>
  );
}
